// CSV du pipeline → Portefeuille validé.
//
// Reprend la construction de poc/src/modele.js, en plus strict : le POC accepte
// n'importe quel CSV déposé à la main et se dégrade en silence ; l'application,
// elle, consomme un contrat (src/donnees/schemas.ts) et s'arrête avec la liste des
// écarts si ce contrat n'est pas tenu.
//
// Règles conservées du POC (poc/docs/adr/0005) :
//   R1 — les référentiels sont de la donnée : la liste des leviers vient de
//        referentiel_leviers.csv, les classes de bioagresseur des colonnes `cible_*` ;
//   R2 — aucun effectif en dur.

import type { z } from "zod";
import { comparerFr } from "../domaine/portefeuille";
import type {
  ClasseBioagresseur,
  Culture,
  Levier,
  Partenaire,
  Portefeuille,
  Projet,
  Signalement,
} from "../domaine/types";
import type { TableCsv } from "./csv";
import { ErreurDonnees, type ProblemeDonnees } from "./erreurs";
import {
  ligneCulture,
  ligneLeviersProjet,
  lignePartenaire,
  ligneProjet,
  ligneReferentielLevier,
  ouiNon,
} from "./schemas";

/** Les tables attendues, sous le nom de fichier publié par scripts/importer-donnees.mjs. */
export const FICHIERS = {
  projets: "projets.csv",
  leviers: "projets_leviers.csv",
  referentielLeviers: "referentiel_leviers.csv",
  cultures: "projets_cultures.csv",
  partenaires: "projets_partenaires.csv",
} as const;

export type NomTable = keyof typeof FICHIERS;
export type TablesCsv = Record<NomTable, TableCsv>;

const PREFIXE_CIBLE = "cible_";
/** Au-delà, la liste n'aide plus à corriger : c'est la source entière qui est en cause. */
const PROBLEMES_MAX = 50;

/**
 * Valeurs séparées par `|`. Une barre entre parenthèses ne sépare rien : le
 * pipeline écrit « Altises (altises du lin|altise d'hiver) » pour un seul nom.
 */
export function liste(v: string): string[] {
  const valeurs: string[] = [];
  let profondeur = 0;
  let courante = "";
  for (const c of v) {
    if (c === "(") profondeur++;
    else if (c === ")") profondeur = Math.max(0, profondeur - 1);
    if (c === "|" && profondeur === 0) {
      valeurs.push(courante);
      courante = "";
    } else courante += c;
  }
  valeurs.push(courante);
  return valeurs.map((x) => x.trim()).filter(Boolean);
}
const ouNull = (v: string): string | null => (v === "" ? null : v);
const capitaliser = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const cleParDefaut = (v: string): string => v.toLowerCase();

/** Rend l'adresse si c'est un lien http(s) valide, `null` sinon (y compris `javascript:`). */
function lienHttp(v: string): string | null {
  if (!URL.canParse(v)) return null;
  const { protocol } = new URL(v);
  return protocol === "http:" || protocol === "https:" ? v : null;
}

/** Une ligne conforme à son schéma. `numero` compte l'en-tête comme ligne 1. */
interface Valide<T> {
  numero: number;
  brute: Record<string, string>;
  donnees: T;
}

class Collecteur {
  readonly problemes: ProblemeDonnees[] = [];

  signaler(p: ProblemeDonnees): void {
    if (this.problemes.length < PROBLEMES_MAX) this.problemes.push(p);
  }

  /** Vérifie la présence des colonnes ; rend `false` si la table est inutilisable. */
  colonnes(table: NomTable, csv: TableCsv, attendues: readonly string[]): boolean {
    const manquantes = attendues.filter((c) => !csv.colonnes.includes(c));
    for (const c of manquantes) {
      this.signaler({ table: FICHIERS[table], ligne: 1, colonne: c, message: "colonne absente" });
    }
    return manquantes.length === 0;
  }

  /** Valide chaque ligne ; ne rend que les lignes conformes, avec leur numéro et leur forme brute. */
  lignes<S extends z.ZodObject>(table: NomTable, csv: TableCsv, schema: S): Valide<z.infer<S>>[] {
    const valides: Valide<z.infer<S>>[] = [];
    csv.lignes.forEach((brute, i) => {
      const resultat = schema.safeParse(brute);
      if (resultat.success) {
        valides.push({ numero: i + 2, brute, donnees: resultat.data });
        return;
      }
      for (const issue of resultat.error.issues) {
        this.signaler({
          table: FICHIERS[table],
          ligne: i + 2,
          colonne: issue.path.map(String).join("."),
          message: issue.message,
        });
      }
    });
    return valides;
  }
}

/**
 * Construit le portefeuille et lève une `ErreurDonnees` de nature `validation` au
 * premier écart au contrat — en listant tous les écarts trouvés, pas seulement le premier.
 */
export function construirePortefeuille(tables: TablesCsv): Portefeuille {
  const c = new Collecteur();

  const colonnesOk = [
    c.colonnes("projets", tables.projets, Object.keys(ligneProjet.shape)),
    c.colonnes("leviers", tables.leviers, Object.keys(ligneLeviersProjet.shape)),
    c.colonnes(
      "referentielLeviers",
      tables.referentielLeviers,
      Object.keys(ligneReferentielLevier.shape),
    ),
    c.colonnes("cultures", tables.cultures, Object.keys(ligneCulture.shape)),
    c.colonnes("partenaires", tables.partenaires, Object.keys(lignePartenaire.shape)),
  ].every(Boolean);
  if (!colonnesOk) throw echec(c);

  // --- Référentiel des leviers (R1)
  const leviers: Levier[] = c
    .lignes("referentielLeviers", tables.referentielLeviers, ligneReferentielLevier)
    .map(({ donnees: r }) => ({
      id: r.id,
      libelle: r.libelle,
      libelleLong: r.libelle_source || r.libelle,
      exemples: liste(r.exemples),
      nature: r.nature || "levier",
    }));
  verifierUnicite(c, "referentielLeviers", leviers, (l) => l.id, "id");

  // Les colonnes de leviers doivent correspondre exactement au référentiel : une
  // colonne inconnue serait ignorée en silence, une colonne absente lue « non ».
  const colonnesLeviers = tables.leviers.colonnes.filter(
    (col) => !(col in ligneLeviersProjet.shape),
  );
  for (const l of leviers) {
    if (!colonnesLeviers.includes(l.id)) {
      c.signaler({
        table: FICHIERS.leviers,
        ligne: 1,
        colonne: l.id,
        message: "levier du référentiel sans colonne",
      });
    }
  }
  for (const col of colonnesLeviers) {
    if (!leviers.some((l) => l.id === col)) {
      c.signaler({
        table: FICHIERS.leviers,
        ligne: 1,
        colonne: col,
        message: "colonne absente du référentiel des leviers",
      });
    }
  }

  // --- Projets
  const classes = tables.projets.colonnes
    .filter((col) => col.startsWith(PREFIXE_CIBLE))
    .map((col) => col.slice(PREFIXE_CIBLE.length));
  const lignesProjets = c.lignes("projets", tables.projets, ligneProjet);
  for (const { numero, brute, donnees: r } of lignesProjets) {
    for (const classe of classes) {
      const col = PREFIXE_CIBLE + classe;
      if (!ouiNon.safeParse(brute[col]).success) {
        c.signaler({
          table: FICHIERS.projets,
          ligne: numero,
          colonne: col,
          message: "attendu « oui » ou « non »",
        });
      }
    }
    if (r.date_debut && r.date_fin && r.date_debut > r.date_fin) {
      c.signaler({
        table: FICHIERS.projets,
        ligne: numero,
        colonne: "date_fin",
        message: "date de fin antérieure à la date de début",
      });
    }
  }
  verifierUnicite(c, "projets", lignesProjets, (r) => r.donnees.ref, "ref");
  verifierUnicite(c, "projets", lignesProjets, (r) => r.donnees.id, "id");
  // Toutes les références présentes, y compris celles des lignes invalides : une
  // ligne de projet en erreur ne doit pas rendre « inconnues » toutes ses lignes filles.
  const refsConnues = new Set(tables.projets.lignes.map((l) => l.ref ?? "").filter(Boolean));

  const signalements: Signalement[] = [];
  const liens = new Map<string, string | null>();
  for (const { numero, donnees: r } of lignesProjets) {
    const lien = r.lien_ecophytopic ? lienHttp(r.lien_ecophytopic) : null;
    liens.set(r.ref, lien);
    if (r.lien_ecophytopic && !lien) {
      signalements.push({
        table: FICHIERS.projets,
        ligne: numero,
        colonne: "lien_ecophytopic",
        message: `${r.acronyme} : « ${r.lien_ecophytopic} » n'est pas une adresse http(s), lien non affiché`,
      });
    }
  }

  // --- Tables filles, jointes par `ref` : c'est pour cela que le pipeline l'a créée
  const coches = new Map<string, Record<string, string>>();
  for (const { numero, brute, donnees } of c.lignes(
    "leviers",
    tables.leviers,
    ligneLeviersProjet,
  )) {
    const ref = donnees.ref_projet;
    if (!refsConnues.has(ref)) {
      c.signaler({
        table: FICHIERS.leviers,
        ligne: numero,
        colonne: "ref_projet",
        message: `projet inconnu : ${ref}`,
      });
    } else if (coches.has(ref)) {
      c.signaler({
        table: FICHIERS.leviers,
        ligne: numero,
        colonne: "ref_projet",
        message: `projet en double : ${ref}`,
      });
    }
    coches.set(ref, brute);
    for (const l of leviers) {
      if (colonnesLeviers.includes(l.id) && !ouiNon.safeParse(brute[l.id]).success) {
        c.signaler({
          table: FICHIERS.leviers,
          ligne: numero,
          colonne: l.id,
          message: "attendu « oui » ou « non »",
        });
      }
    }
  }
  for (const ref of refsConnues) {
    if (!coches.has(ref)) {
      c.signaler({ table: FICHIERS.leviers, message: `aucune ligne pour le projet ${ref}` });
    }
  }

  const cultures = grouperParProjet(
    c,
    "cultures",
    c.lignes("cultures", tables.cultures, ligneCulture),
    refsConnues,
    (r): Culture => ({ nom: r.culture, cle: r.culture_cle || cleParDefaut(r.culture) }),
  );
  const partenaires = grouperParProjet(
    c,
    "partenaires",
    c.lignes("partenaires", tables.partenaires, lignePartenaire),
    refsConnues,
    (r): Partenaire => ({
      nom: r.partenaire,
      cle: r.partenaire_cle || cleParDefaut(r.partenaire),
      typeStructure: ouNull(r.type_structure),
    }),
  );

  if (c.problemes.length) throw echec(c);

  const projets: Projet[] = lignesProjets.map(({ brute, donnees: r }) => {
    const cibles = classes.filter((classe) => brute[PREFIXE_CIBLE + classe] === "oui");
    const [unique] = cibles;
    const ligneLeviers = coches.get(r.ref) ?? {};
    return {
      ref: r.ref,
      id: r.id,
      acronyme: r.acronyme,
      titre: r.titre,
      porteur: r.porteur,
      typePorteur: ouNull(r.type_porteur),
      portee: r.portee || null,
      filiere: ouNull(r.filiere),
      filieresImpliquees: liste(r.filieres_impliquees),
      planAction: ouNull(r.plan_action),
      bioagresseurs: cibles,
      bioagresseurGroupe: cibles.length > 1 ? "mixte" : (unique ?? "inconnu"),
      bioagresseursCommuns: liste(r.bioagresseurs_communs),
      bioagresseursLatins: liste(r.bioagresseurs_latins),
      dateDebut: ouNull(r.date_debut),
      dateFin: ouNull(r.date_fin),
      dureeMois: r.duree_mois ? Number(r.duree_mois) : null,
      lienFiche: liens.get(r.ref) ?? null,
      leviers: leviers.filter((l) => ligneLeviers[l.id] === "oui").map((l) => l.id),
      cultures: cultures.get(r.ref) ?? [],
      partenaires: partenaires.get(r.ref) ?? [],
    };
  });

  const distincts = (valeurs: (string | null)[]): string[] =>
    [...new Set(valeurs.filter((v): v is string => Boolean(v)))].sort(comparerFr);
  const classesBioagresseur: ClasseBioagresseur[] = classes.map((id) => ({
    id,
    libelle: capitaliser(id),
  }));

  return {
    projets,
    leviers,
    classesBioagresseur,
    filieres: distincts(projets.map((p) => p.filiere)),
    typesPorteur: distincts(projets.map((p) => p.typePorteur)),
    dimensions: {
      leviers: projets.some((p) => p.leviers.length > 0),
      cultures: projets.some((p) => p.cultures.length > 0),
      bioagresseurs: projets.some((p) => p.bioagresseurs.length > 0),
      partenaires: projets.some((p) => p.partenaires.length > 0),
    },
    signalements,
  };
}

function echec(c: Collecteur): ErreurDonnees {
  const n = c.problemes.length;
  const plus = n >= PROBLEMES_MAX ? " (liste tronquée)" : "";
  return new ErreurDonnees(
    "validation",
    `Les données ne respectent pas le format attendu : ${n} problème${n > 1 ? "s" : ""}${plus}.`,
    c.problemes,
  );
}

function verifierUnicite<T>(
  c: Collecteur,
  table: NomTable,
  elements: T[],
  cle: (e: T) => string,
  colonne: string,
): void {
  const vus = new Set<string>();
  elements.forEach((e) => {
    const k = cle(e);
    if (vus.has(k)) {
      c.signaler({ table: FICHIERS[table], colonne, message: `valeur en double : ${k}` });
    }
    vus.add(k);
  });
}

function grouperParProjet<L extends { ref_projet: string }, T>(
  c: Collecteur,
  table: NomTable,
  lignes: Valide<L>[],
  refsConnues: ReadonlySet<string>,
  transformer: (ligne: L) => T,
): Map<string, T[]> {
  const index = new Map<string, T[]>();
  for (const { numero, donnees } of lignes) {
    if (!refsConnues.has(donnees.ref_projet)) {
      c.signaler({
        table: FICHIERS[table],
        ligne: numero,
        colonne: "ref_projet",
        message: `projet inconnu : ${donnees.ref_projet}`,
      });
      continue;
    }
    const groupe = index.get(donnees.ref_projet) ?? [];
    groupe.push(transformer(donnees));
    index.set(donnees.ref_projet, groupe);
  }
  return index;
}
